import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check, Search } from 'lucide-react';
import { TeeInput } from '@/components/ui/tee-input';
import TeeLoader from '@/components/ui/tee-loader';
import { locationService } from '@/services/location.service';
import { LocationSummaryDto } from '@/types/location';

export interface LocationSwitcherProps {
  currentLocationId: string;
  currentLocationName: string;
  onSelect: (locationId: string) => void;
}

export function LocationSwitcher({
  currentLocationId,
  currentLocationName,
  onSelect,
}: LocationSwitcherProps) {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  // null = not yet fetched; array = cached result
  const [locations, setLocations] = useState<LocationSummaryDto[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const triggerRef = useRef<HTMLButtonElement>(null);
  // Ref-based fetch guard: prevents double-fetch even while the first fetch is in-flight
  const fetchedRef = useRef(false);

  // Fetch once, cache for page lifecycle
  const fetchLocations = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await locationService.getSwitcherLocations();
      setLocations(data);
    } catch {
      setError(t('locationSwitcher.fetchError'));
      // Reset guard on error so retry is possible
      fetchedRef.current = false;
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setQuery('');
    setHighlightedIndex(-1);
    // Fetch only once — ref-based cache guard (works even during in-flight requests)
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchLocations();
    }
  }, [fetchLocations]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, []);

  const handleTriggerClick = () => {
    if (isOpen) {
      handleClose();
    } else {
      handleOpen();
    }
  };

  // 200ms delay allows list-item click events to fire before blur closes the panel
  const handleBlur = () => {
    setTimeout(() => {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }, 200);
  };

  const handleRetry = () => {
    setError(null);
    fetchLocations();
  };

  // Build filtered + sorted list: current location first, then alpha-sorted
  const filtered: LocationSummaryDto[] = (() => {
    if (!locations) return [];
    const lower = query.toLowerCase();
    const matches = locations.filter((loc) =>
      loc.name.toLowerCase().includes(lower)
    );
    const current = matches.find((loc) => loc.id === currentLocationId);
    const others = matches
      .filter((loc) => loc.id !== currentLocationId)
      .sort((a, b) => a.name.localeCompare(b.name));
    return current ? [current, ...others] : others;
  })();

  const handleSelect = (loc: LocationSummaryDto) => {
    if (loc.id === currentLocationId) {
      // Clicking current location just closes the panel
      handleClose();
      return;
    }
    onSelect(loc.id);
    handleClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      const loc = filtered[highlightedIndex];
      if (loc) handleSelect(loc);
    } else if (e.key === 'Escape') {
      handleClose();
      triggerRef.current?.focus();
    }
  };

  return (
    <div className="relative inline-flex items-center">
      {/* Trigger button — chip-pattern approved exception to raw <button> rule */}
      <button
        ref={triggerRef}
        type="button"
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t('locationSwitcher.triggerAriaLabel', { name: currentLocationName })}
        onClick={handleTriggerClick}
        className="inline-flex items-center gap-2 min-h-[44px] px-1 py-1 rounded-md shrink-0
          text-[var(--text-content-title)]
          hover:bg-[var(--background-card-hover)] transition-colors focus:outline-none
          focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
      >
        <span className="whitespace-nowrap">{currentLocationName}</span>
        <ChevronDown
          size={22}
          aria-hidden="true"
          style={{ color: 'var(--color-text-gold)' }}
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Floating panel */}
      {isOpen && (
        <div
          role="listbox"
          aria-label={t('locationSwitcher.searchAriaLabel')}
          className="absolute top-full left-0 mt-1 min-w-[280px] max-w-[480px] z-50
            rounded-md border border-[var(--border-default)] bg-[var(--background-surface-floating)]
            shadow-lg"
        >
          {/* Search input */}
          <div className="p-2">
            <TeeInput
              inputSize="s"
              placeholder={t('locationSwitcher.searchPlaceholder')}
              aria-label={t('locationSwitcher.searchAriaLabel')}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setHighlightedIndex(-1);
              }}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              autoFocus
              leadingIcon={<Search size={16} className="text-neutral-400" />}
            />
          </div>

          {/* Divider */}
          <div className="h-px bg-[var(--border-default)]" />

          {/* List body */}
          <div className="max-h-80 overflow-y-auto py-1">
            {isLoading && (
              <div className="flex justify-center items-center py-6">
                <TeeLoader size="sm" />
              </div>
            )}

            {!isLoading && error && (
              <div className="px-3 py-4 text-center">
                <p className="text-[14px] text-[var(--text-content-sub-title)] mb-2">
                  {error}
                </p>
                <button
                  type="button"
                  onClick={handleRetry}
                  className="text-[14px] font-medium text-[var(--color-text-gold)]
                    hover:underline focus:outline-none"
                >
                  {t('locationSwitcher.retryLabel')}
                </button>
              </div>
            )}

            {!isLoading && !error && filtered.length === 0 && (
              <p className="px-3 py-4 text-center text-[14px] text-[var(--text-content-sub-title)]">
                {t('locationSwitcher.emptyResult')}
              </p>
            )}

            {!isLoading && !error && filtered.map((loc, idx) => {
              const isCurrent = loc.id === currentLocationId;
              const isHighlighted = idx === highlightedIndex;
              return (
                <button
                  key={loc.id}
                  type="button"
                  role="option"
                  aria-selected={isCurrent}
                  onClick={() => handleSelect(loc)}
                  className={`
                    w-full flex items-center justify-between px-3 py-2 text-left
                    text-[14px] transition-colors focus:outline-none
                    ${isHighlighted ? 'bg-[var(--background-card-hover)]' : 'hover:bg-[var(--background-card-hover)]'}
                    ${isCurrent ? 'font-semibold text-[var(--text-content-title)]' : 'font-normal text-[var(--text-content-title)]'}
                  `}
                >
                  <span>{loc.name}</span>
                  {isCurrent && (
                    <Check
                      size={16}
                      aria-hidden="true"
                      style={{ color: 'var(--color-text-gold)', flexShrink: 0 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
